import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { CalendarCheck, PlayCircle, RefreshCw } from 'lucide-react';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import {
  DUE_STATUS_LABELS,
  FREQUENCY_LABELS,
  useMaintenanceDue,
} from '@/hooks/useMaintenanceDue';
import { formatDate, formatDateTime } from '@/lib/format';
import type { MaintenanceDueRow, MaintenanceDueWindow } from '@/types/api';

/**
 * /maintenance/due - Fälligkeitsliste: welche Kunden gehören gewartet.
 *
 * Sortierung kommt fertig vom Backend (Status -> due_date -> Wartungsfenster
 * -> Name) und wird hier NICHT umsortiert. Default zeigt nur overdue +
 * due_soon; der Toggle "Alle Kunden anzeigen" lädt mit include_ok=true nach.
 * Zeilen-Aktion "Run planen" springt vorbefüllt in den Run-Wizard
 * (/runs/new?customers=<id>, Search-Schema siehe runs/new.tsx).
 */

const WITHIN_DAYS_OPTIONS = [7, 14, 30, 90] as const;

export const Route = createFileRoute('/maintenance/due')({
  component: MaintenanceDuePage,
});

function MaintenanceDuePage(): JSX.Element {
  const [withinDays, setWithinDays] = useState<number>(14);
  const [includeOk, setIncludeOk] = useState(false);

  const dueQuery = useMaintenanceDue({ withinDays, includeOk });
  const rows = useMemo(() => dueQuery.data?.data ?? [], [dueQuery.data]);
  const meta = dueQuery.data?.meta;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Fällige Wartungen</h1>
          <p className="text-muted">
            Berechnet aus letztem erfolgreichem Apply-Run und Wartungsfrequenz.
            Stand:{' '}
            {meta?.generated_at ? formatDateTime(meta.generated_at) : '–'}
          </p>
        </div>
        <Button
          variant="secondary"
          leftIcon={<RefreshCw className="size-4" />}
          onClick={() => void dueQuery.refetch()}
          loading={dueQuery.isFetching}
        >
          Aktualisieren
        </Button>
      </header>

      <Card>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Horizont</span>
              <select
                value={withinDays}
                onChange={(e) => setWithinDays(Number(e.target.value))}
                className="min-w-[160px] rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="due-filter-within-days"
              >
                {WITHIN_DAYS_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    Nächste {d} Tage
                  </option>
                ))}
              </select>
            </label>
            <label className="flex cursor-pointer items-center gap-2 pb-2 text-sm">
              <input
                type="checkbox"
                checked={includeOk}
                onChange={(e) => setIncludeOk(e.target.checked)}
                className="size-4 rounded border-border accent-primary"
                data-testid="due-filter-include-ok"
              />
              Alle Kunden anzeigen
            </label>
          </div>
          {meta && (
            <div className="flex flex-wrap items-center gap-2 pb-1">
              <Badge tone={meta.overdue > 0 ? 'danger' : 'neutral'}>
                {meta.overdue} überfällig
              </Badge>
              <Badge tone={meta.due_soon > 0 ? 'warning' : 'neutral'}>
                {meta.due_soon} bald fällig
              </Badge>
              <Badge tone="neutral">{meta.ok} ok</Badge>
              {meta.on_demand_excluded + meta.no_systems_excluded > 0 && (
                <span className="text-xs text-muted">
                  {meta.on_demand_excluded + meta.no_systems_excluded} Kunden
                  ausgeklammert (auf Abruf / ohne Systeme)
                </span>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card title="Fälligkeitsliste">
        {dueQuery.isLoading && <SkeletonRows rows={6} cols={7} />}
        {dueQuery.isError && <ErrorAlert error={dueQuery.error} />}
        {!dueQuery.isLoading && !dueQuery.isError && rows.length === 0 && (
          <EmptyState
            icon={<CalendarCheck className="size-8" />}
            title={includeOk ? 'Keine Kunden in der Liste' : 'Nichts fällig'}
            description={
              includeOk
                ? 'Es gibt keine Kunden mit wartbaren Systemen und geplanter Frequenz.'
                : 'Alles gewartet - im gewählten Horizont ist kein Kunde fällig.'
            }
          />
        )}
        {rows.length > 0 && <DueTable rows={rows} />}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabelle
// ---------------------------------------------------------------------------

function ohneStrich(text: string): string {
  return text === '–' ? '' : text;
}

function dueStatusText(row: MaintenanceDueRow): string {
  if (row.never_maintained) return 'Nie gewartet';
  return DUE_STATUS_LABELS[row.due_status] ?? row.due_status;
}

function zuletztGewartetText(row: MaintenanceDueRow): string {
  return row.last_maintained_at ? formatDate(row.last_maintained_at) : 'Noch nie';
}

const DUE_COLUMNS: ReadonlyArray<Column<MaintenanceDueRow>> = [
  {
    id: 'kunde',
    header: 'Kunde',
    filterValue: (row) => `${row.customer_name} ${row.customer_slug}`,
    cell: (row) => (
      <>
        <Link
          to="/customers/$id"
          params={{ id: row.customer_id }}
          className="text-primary hover:underline"
        >
          {row.customer_name}
        </Link>
        <p className="font-mono text-xs text-muted">{row.customer_slug}</p>
      </>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    filter: 'select',
    filterValue: dueStatusText,
    cell: (row) => <DueStatusBadge row={row} />,
  },
  {
    id: 'faellig-am',
    header: 'Fällig am',
    className: 'text-xs',
    filterValue: (row) =>
      [ohneStrich(formatDate(row.due_date)), daysHint(row.days_until_due)]
        .filter(Boolean)
        .join(' '),
    cell: (row) => (
      <>
        {formatDate(row.due_date)}
        <p className="text-muted">{daysHint(row.days_until_due)}</p>
      </>
    ),
  },
  {
    id: 'zuletzt-gewartet',
    header: 'Zuletzt gewartet',
    className: 'text-xs',
    filterValue: (row) => ohneStrich(zuletztGewartetText(row)),
    cell: (row) => zuletztGewartetText(row),
  },
  {
    id: 'frequenz',
    header: 'Frequenz',
    className: 'text-xs',
    filter: 'select',
    filterValue: (row) => FREQUENCY_LABELS[row.frequency] ?? row.frequency,
    cell: (row) => (
      <>
        {FREQUENCY_LABELS[row.frequency] ?? row.frequency}
        <p className="text-muted">
          {row.frequency_source === 'plan' ? 'aus Wartungsplan' : 'aus Kundendaten'}
        </p>
      </>
    ),
  },
  {
    id: 'wartungsfenster',
    header: 'Wartungsfenster',
    className: 'text-xs',
    filterValue: (row) => ohneStrich(formatWindow(row.maintenance_window)),
    cell: (row) => formatWindow(row.maintenance_window),
  },
  {
    id: 'aktion',
    header: 'Aktion',
    filter: false,
    headerClassName: 'text-right',
    className: 'text-right',
    cell: (row) => (
      <Link
        to="/runs/new"
        search={{ customers: row.customer_id }}
        data-testid={`due-plan-run-${row.customer_slug}`}
      >
        <Button size="sm" variant="ghost" leftIcon={<PlayCircle className="size-3" />}>
          Run planen
        </Button>
      </Link>
    ),
  },
];

function DueTable({ rows }: { rows: MaintenanceDueRow[] }): JSX.Element {
  return (
    <DataTable
      caption="Fällige Wartungen"
      columns={DUE_COLUMNS}
      rows={rows}
      rowKey={(row) => row.customer_id}
      storageKey="faellige-wartungen"
      rowTestId={(row) => `due-row-${row.customer_slug}`}
    />
  );
}

function DueStatusBadge({ row }: { row: MaintenanceDueRow }): JSX.Element {
  // "Nie gewartet" ist Teilmenge von overdue, aber eigenes Label -
  // safety-first-Konvention aus der Fälligkeits-Logik.
  if (row.never_maintained) {
    return <Badge tone="danger">Nie gewartet</Badge>;
  }
  const tone: BadgeTone =
    row.due_status === 'overdue'
      ? 'danger'
      : row.due_status === 'due_soon'
        ? 'warning'
        : 'success';
  return <Badge tone={tone}>{DUE_STATUS_LABELS[row.due_status]}</Badge>;
}

// ---------------------------------------------------------------------------
// Format-Helfer (nur für diese Seite)
// ---------------------------------------------------------------------------

function daysHint(daysUntilDue: number): string {
  if (daysUntilDue < 0) {
    const n = Math.abs(daysUntilDue);
    return n === 1 ? 'seit 1 Tag' : `seit ${n} Tagen`;
  }
  if (daysUntilDue === 0) return 'heute';
  if (daysUntilDue === 1) return 'in 1 Tag';
  return `in ${daysUntilDue} Tagen`;
}

/** Backend-Wire-Codes (mon..sun) -> deutsche Kurzlabels. */
const DAY_LABELS: Record<string, string> = {
  mon: 'Mo',
  tue: 'Di',
  wed: 'Mi',
  thu: 'Do',
  fri: 'Fr',
  sat: 'Sa',
  sun: 'So',
};

/** z.B. "Di, Do 18:00–22:00"; "–" wenn kein Fenster gepflegt. */
function formatWindow(window: MaintenanceDueWindow | null): string {
  if (!window) return '–';
  const days = (window.days ?? [])
    .map((d) => DAY_LABELS[d.toLowerCase()] ?? d)
    .join(', ');
  const time =
    window.start && window.end
      ? `${window.start}–${window.end}`
      : window.start ?? '';
  const label = [days, time].filter(Boolean).join(' ');
  return label || '–';
}
